import React from "react";
import Config from "src/Constants/Config";

export interface DualListProps {
  pageId?: number;
  basePath?: string; // Optional override for image base path
  topListItems?: string[];
  bottomListItems?: string[];
  topImageSrc?: string;
  bottomImageSrc?: string;
  separatorImageSrc?: string;
  topPersonName?: string;
  bottomPersonName?: string;
  topListTitle?: string;
  bottomListTitle?: string;
  topImageScale?: number; // Scale factor for the top image (default: 0.5)
  bottomImageScale?: number; // Scale factor for the bottom image (default: 0.5)
  separatorImageScale?: number; // Scale factor for the separator image (default: 0.5)
}

const DualList: React.FC<DualListProps> = ({
  pageId,
  basePath,
  topListItems = ["Personalized Service", "Proactive Outreach", "Consistent Quality"],
  bottomListItems = ["Seamless Experience", "Agentic AI for Automation", "Copilot for Operations", "Proactive Nudges"],
  topImageSrc,
  bottomImageSrc,
  separatorImageSrc,
  topPersonName = "Martina",
  bottomPersonName = "Sarah",
  topListTitle = "Top List Title",
  bottomListTitle = "Bottom List Title",
  topImageScale = 0.05,
  bottomImageScale = 0.05,
  separatorImageScale = 0.345,
}) => {
  // Determine the base path either from props or using the pageId
  const computedBasePath = basePath || (pageId ? `${Config.BASE_URL}/images/screen_${pageId}/` : "");

  // Use provided image sources or fallback to defaults based on the base path
  const computedTopImageSrc = topImageSrc || `${computedBasePath}${topPersonName}.png`;
  const computedBottomImageSrc = bottomImageSrc || `${computedBasePath}${bottomPersonName}.png`;
  const computedSeparatorImageSrc = separatorImageSrc || `${computedBasePath}left_separator.png`;

  // Shared text style for list items and person names
  const textStyle: React.CSSProperties = {
    color: "#FFF",
    fontFamily: "Myriad Pro",
    fontSize: "var(--width-22)",
    fontStyle: "normal",
    fontWeight: 300,
    lineHeight: "150%",
    marginTop: 0,
  };

  // Slightly bolder style for list titles
  const titleStyle: React.CSSProperties = {
    ...textStyle,
    fontWeight: 700,
  };

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: "var(--width-16)",
        width: "100%",
        paddingTop: "var(--width-40)",
      }}
    >
      {/* Top List */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: "var(--width-24)", // Gap between image and list
          width: "100%",
        }}
      >
        <img
          src={computedTopImageSrc}
          alt={topPersonName}
          style={{
            width: `${topImageScale * 100}%`, // Scale applied directly
            height: "auto", // Maintain aspect ratio
          }}
        />
        <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>
          <span
            style={{
              ...titleStyle,
              marginLeft: "var(--width-16)", // Indent to match list items
            }}
          >
            {topListTitle}
          </span>
          <ul
            style={{
              margin: 0,
              paddingLeft: "var(--width-16)", // Matches the bullet indent
              listStylePosition: "inside", // Aligns bullet with text
            }}
          >
            {topListItems.map((item, index) => (
              <li key={`bottom-${index}`} style={textStyle}>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Separator */}
      <div style={{ display: "flex", justifyContent: "flex-start", width: "100%" }}>
        <img
          src={computedSeparatorImageSrc}
          alt="Separator"
          style={{
            width: `${separatorImageScale * 100}%`, // Scale applied directly
            height: "auto", // Maintain aspect ratio
          }}
        />
      </div>

      {/* Bottom List */}
      <div
        style={{
          display: "flex",
          alignItems: "flex-start",
          gap: "var(--width-24)", // Gap between image and list
          width: "100%",
        }}
      >
        <img
          src={computedBottomImageSrc}
          alt={bottomPersonName}
          style={{
            width: `${bottomImageScale * 100}%`, // Scale applied directly
            height: "auto", // Maintain aspect ratio
          }}
        />
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
          }}
        >
          <span
            style={{
              ...titleStyle,
              marginLeft: "var(--width-16)", // Indent to match list items
            }}
          >
            {bottomListTitle}
          </span>
          <ul
            style={{
              margin: 0,
              paddingLeft: "var(--width-16)", // Matches the bullet indent
              listStylePosition: "inside", // Aligns bullet with text
            }}
          >
            {bottomListItems.map((item, index) => (
              <li key={`bottom-${index}`} style={textStyle}>
                {item}
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

export default DualList;
